// ---------------------------------------------------------
// Datei: hubspotDealMapper.ts
// Zweck: Mappt HubSpot-Deal-Daten zu Projektphasen und Dokumenten-Status
// Besonderheiten:
// - Extrahiert Dokumenten-Status aus Deal-Properties
// - Berechnet Progress für jede Phase
// - Unterstützt die HubSpot-Phasen inkl. Baustellen-Abschlussbericht
// Stand: 14.  Januar 2026
// ---------------------------------------------------------

import { HubSpotDeal } from "../types/hubspot";

// Typ für gemappte Phase
export interface MappedPhase {
  _id: string;
  name:  string;
  description: string;
  status: string;
  completionPercent?: number;
  completedAt?: string;
  completedByEmail?: string;
  approvedByEmail?: string;
  project: {
    _id: string;
    name: string;
  };
}

/**
 * Mappt einen HubSpot-Deal zu den Projektphasen
 * 
 * Warum diese Phasen?
 * - Projektdokumentation / interne Freigabe: strukturierte Sales→OPS-Freigabe
 * - Durchführung: Baustellenfotos während Installation
 * - Inbetriebnahme: Abschlussdokumente und Protokolle
 * - Baustellen-Abschlussbericht: strukturierter Wizard auf Basis der Standard-AB
 * - Störungen: Störungstickets mit Foto + Text an Make.com Webhook (Issue #68)
 */
export function mapDealToPhases(deal: HubSpotDeal): MappedPhase[] {
  const projectStatusMap = new Map(
    (deal.project_status?.phases || []).map((phase) => [phase.phaseKey, phase])
  );
  const getPhaseMeta = (phaseKey: 'projektdokumentation' | 'durchfuehrung' | 'inbetriebnahme' | 'abschlussbericht') => {
    const phaseStatus = projectStatusMap.get(phaseKey);
    return {
      status: phaseStatus?.status === "completed" ? "completed" : "open",
      completionPercent: phaseStatus?.completionPercent || 0,
      completedAt: phaseStatus?.completedAt || undefined,
      completedByEmail: phaseStatus?.completedByEmail || undefined,
      approvedByEmail: phaseStatus?.approvedByEmail || undefined,
    };
  };

  return [
    {
      ...getPhaseMeta("projektdokumentation"),
      _id: `${deal.id}-projektdokumentation`,
      name: "Projektdokumentation / interne Freigabe",
      description: "Interne Prüfung & Freigabe von Sales an OPS",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
    {
      ...getPhaseMeta("durchfuehrung"),
      _id: `${deal.id}-durchfuehrung`,
      name: "Baustellendoku / Fotos",
      description: "Durchführungsdokumentation",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
    {
      ...getPhaseMeta("inbetriebnahme"),
      _id: `${deal.id}-inbetriebnahme`,
      name: "Inbetriebnahme",
      description: "Inbetriebnahme-Dokumentation",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
    {
      ...getPhaseMeta("abschlussbericht"),
      _id: `${deal.id}-abschlussbericht`,
      name: "Baustellen-Abschlussbericht",
      description: "Abschlussbericht auf Basis der Auftragsbestätigung",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
    {
      _id: `${deal.id}-uploads`,
      name: "Allgemeine Uploads",
      description: "Fotos & Dokumente ohne Phasenzuordnung",
      status: "open",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
    {
      // Neue Phase für Nacharbeiten/Störungen (Issue #68)
      _id: `${deal.id}-stoerungen`,
      name: "Nacharbeiten/Störungen melden",
      description: "Störungen und Nacharbeiten mit Foto und Beschreibung dokumentieren",
      status: "open",
      project: {
        _id: deal.id,
        name: deal.dealname || "Unbekanntes Projekt",
      },
    },
  ];
}

// Mapping:  Document-Type-ID → HubSpot Property Name
// Warum?  Frontend nutzt kebab-case IDs, HubSpot nutzt snake_case Properties
export const DOCUMENT_TO_PROPERTY_MAP:  Record<string, string> = {
  // Durchführung
  "typenschild-altanlage-durchfuehrung": "typenschild_altanlage",
  "ausseneinheit-fundament": "ausseneinheit_fundament",
  "inneneinheit-rohranschluss": "inneneinheit_rohranschluss",
  "tw-speicher-sicherheitsgruppe": "tw_speicher_mit_sicherheitsgruppe",
  "ablauf-ausblaseleitung-sicherheitsventil":  "abaluf_ausblaseleitung_sicherheitsventil", // HubSpot-Property hat Typo "abaluf"
  "elektrische-absicherung-wp":  "elektrische_absicherung_wp",
  "anschluss-pumpengruppe-heizkreis": "anschluss_pumpengruppe_an_heizkreis",
  "komplettaufnahme-inneneinheit": "komplettaufnahme_inneneinheit",
  "verschlossene-gasleitung": "verschlossene_gasleitung",
  "seriennummer-ausseneinheit": "seriennummer_der_warmepumpe",
  // Inbetriebnahme
  "vdz-formular": "vdz_formular",
  "inbetriebnahmeprotokoll":  "inbetriebnahmeprotokoll",
  "garantie-5-jahre": "garantie5jahre",
  "anlagenbuch": "anlagenbuch",
  "technische-abweichung": "technische_abweichung",
  "seriennummer-registrieren": "seriennummer_der_warmepumpe",
};


/**
 * Extrahiert Dokumenten-Status aus Deal-Properties mit Typ-Informationen
 * 
 * Wie es funktioniert:
 * 1. Filtere document-types. json nach phaseId
 * 2. Prüfe für jedes Dokument ob HubSpot-Property einen Wert hat
 * 3. Setze Status:  completed (Wert vorhanden) oder pending (leer)
 */
export function extractDocumentStatusWithTypes(
  deal: HubSpotDeal,
  phaseId: string
): Array<{
  _id: string;
  name:  string;
  status: "pending" | "completed";
  fileId?:  string;
  fileIds?: string[];
  required:  boolean;
  multiUpload?: boolean;
}> {
  // Phase-Name aus phaseId extrahieren (z.B. "123-vorab" → "vorab")
  const phaseName = phaseId.split("-").pop() || "";
  
  // Document-Types für diese Phase (aus statischer Konfiguration)
  const documentTypesForPhase = getDocumentTypesForPhase(phaseName);
  
  return documentTypesForPhase.map((docType) => {
    const propertyName = DOCUMENT_TO_PROPERTY_MAP[docType. id] || docType.id. replace(/-/g, "_");
    const propertyValue = (deal as any)[propertyName];
    
    // Für Multi-Upload: Parse JSON-Array von FileIDs
    let fileIds: string[] | undefined;
    let fileId: string | undefined;
    
    if (docType.multiUpload && propertyValue) {
      try {
        // Versuche JSON-Array zu parsen
        fileIds = JSON.parse(propertyValue);
        if (! Array.isArray(fileIds)) {
          fileIds = [propertyValue];
        }
      } catch {
        // Fallback:  Einzelne FileID
        fileIds = [propertyValue];
      }
    } else {
      fileId = propertyValue || undefined;
    }
    
    const hasValue = docType.multiUpload
      ? (fileIds && fileIds.length > 0)
      : !!propertyValue;

    // seriennummer-registrieren darf nicht anhand des HubSpot-Property-Werts
    // als completed gelten – nur eine erfolgreiche Webhook-Antwort darf das setzen.
    const isRegistrationPending = docType.id === "seriennummer-registrieren";

    return {
      _id:  docType.id,
      name: docType.title,
      status: (!isRegistrationPending && hasValue) ? "completed" : "pending",
      fileId,
      fileIds,
      required: docType. required,
      multiUpload:  docType.multiUpload,
    };
  });
}

/**
 * Berechnet den Progress einer Phase (Pflichtdokumente)
 */
export function calculatePhaseProgress(
  phaseId: string,
  deal: HubSpotDeal
): { required: number; completed: number } {
  const documents = extractDocumentStatusWithTypes(deal, phaseId);
  const requiredDocs = documents.filter((d) => d.required);
  const completedDocs = requiredDocs.filter((d) => d.status === "completed");
  
  return {
    required: requiredDocs.length,
    completed: completedDocs.length,
  };
}

// Dokumenttypen nach Phase (statische Konfiguration)
// In Produktion würde dies aus config/document-types.json geladen
interface DocumentTypeConfig {
  id: string;
  title: string;
  phaseId: string;
  required: boolean;
  multiUpload?: boolean;
}

function getDocumentTypesForPhase(phaseName: string): DocumentTypeConfig[] {
  // Statische Konfiguration (sollte mit document-types.json synchron sein)
  const allDocTypes: DocumentTypeConfig[] = [
    // Vorab
    { id: "typenschild-altanlage", title: "Typenschild Altanlage", phaseId: "vorab", required: true },
    { id:  "standort-ausseneinheit-geplant", title: "Geplanter Standort Außeneinheit", phaseId: "vorab", required: false },
    { id: "standort-inneneinheit-geplant", title: "Geplanter Standort Inneneinheit", phaseId: "vorab", required: false },
    { id: "gaszaehler", title: "Gaszähler", phaseId:  "vorab", required: false },
    { id: "zaehlerschrank", title: "Zählerschrank", phaseId: "vorab", required: false },
    { id: "treppe", title: "Treppe", phaseId: "vorab", required: false },
    { id:  "zugaenge", title: "Zugänge", phaseId: "vorab", required: false },
    { id: "parkflaeche", title: "Parkfläche", phaseId: "vorab", required: false },
    // Durchführung
    { id: "typenschild-altanlage-durchfuehrung", title: "Typenschild Altanlage", phaseId: "durchfuehrung", required: true },
    { id: "ausseneinheit-fundament", title:  "Außeneinheit + Fundament", phaseId:  "durchfuehrung", required: true },
    { id:  "inneneinheit-rohranschluss", title: "Inneneinheit + Rohranschluss", phaseId:  "durchfuehrung", required: true },
    { id:  "tw-speicher-sicherheitsgruppe", title: "TW Speicher mit Sicherheitsgruppe", phaseId: "durchfuehrung", required: true },
    { id: "ablauf-ausblaseleitung-sicherheitsventil", title: "Ablauf Ausblaseleitung", phaseId: "durchfuehrung", required: false },
    { id: "elektrische-absicherung-wp", title:  "Elektrische Absicherung WP", phaseId: "durchfuehrung", required:  false },
    { id: "anschluss-pumpengruppe-heizkreis", title: "Anschluss Pumpengruppe", phaseId: "durchfuehrung", required: false },
    { id: "komplettaufnahme-inneneinheit", title: "Komplettaufnahme Inneneinheit", phaseId: "durchfuehrung", required: false },
    { id: "verschlossene-gasleitung", title: "Verschlossene Gasleitung", phaseId: "durchfuehrung", required: false },
    { id: "seriennummer-ausseneinheit", title: "Seriennummer Außeneinheit", phaseId: "durchfuehrung", required: false },
    // Inbetriebnahme
    { id: "vdz-formular", title: "VDZ Formular", phaseId: "inbetriebnahme", required: true },
    { id: "inbetriebnahmeprotokoll", title: "Inbetriebnahmeprotokoll", phaseId: "inbetriebnahme", required: true },
    { id: "garantie-5-jahre", title: "Garantie 5 Jahre", phaseId: "inbetriebnahme", required: true },
    { id: "seriennummer-registrieren", title: "Seriennummer Registrieren", phaseId: "inbetriebnahme", required: true },
    { id: "anlagenbuch", title: "Anlagenbuch", phaseId: "inbetriebnahme", required: false },
    { id: "technische-abweichung", title: "Technische Abweichung", phaseId: "inbetriebnahme", required: false },
    // Bericht (NEU)
    { id: "arbeitsbericht", title:  "Arbeitsbericht (Zeittracker)", phaseId: "bericht", required: false, multiUpload: true },
  ];
  
  return allDocTypes. filter((dt) => dt.phaseId === phaseName);
}
